// CTV-445: the campaign performance report is a celebratory, brand-agnostic
// surface. It deliberately does NOT use the publisher's colors — every
// advertiser gets the same energetic "your ad is getting views!" palette so the
// page reads as exciting regardless of which station's branding is muted.
//
// Anchored on green (growth / success / delivery) to match the ZIP heatmap.

export const REPORT_ACCENT = '#16a34a'; // green-600 — toggles, icons, stats, bars

// Shared height (px) for the geographic-section maps. The breakdown list beside
// them matches this height and scrolls when there are more rows than fit.
export const GEO_MAP_HEIGHT = 400;

// Banner / hero gradient (emerald → teal) for the "View performance report" CTA.
export const REPORT_GRADIENT_FROM = '#059669'; // emerald-600
export const REPORT_GRADIENT_TO = '#0d9488'; // teal-600

// Sequential heat scale for the ZIP choropleth (light → dark green). This is the
// default ramp, used whenever the report isn't themed to a brand color.
export const HEAT_LOW = '#e7f6ec';
export const HEAT_MID = '#5cc27e';
export const HEAT_HIGH = '#15803d';

export interface HeatRamp {
  low: string;
  mid: string;
  high: string;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const f = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  return [parseInt(f.slice(0, 2), 16), parseInt(f.slice(2, 4), 16), parseInt(f.slice(4, 6), 16)];
}

// Blend `hex` toward `target` by t (0 = hex, 1 = target).
function mixHex(hex: string, target: string, t: number): string {
  const a = hexToRgb(hex);
  const b = hexToRgb(target);
  const c = (i: number) => Math.round(a[i] + (b[i] - a[i]) * t)
    .toString(16)
    .padStart(2, '0');
  return `#${c(0)}${c(1)}${c(2)}`;
}

// Light → dark sequential heat ramp derived from a brand color, for the report's
// choropleths. Returns the tuned default green ramp when there's no usable brand
// color (or it's exactly the default accent) so the un-themed look is unchanged.
export function heatRamp(hex?: string | null): HeatRamp {
  if (!hex || !/^#[0-9a-f]{6}$/i.test(hex) || hex.toLowerCase() === REPORT_ACCENT.toLowerCase()) {
    return { low: HEAT_LOW, mid: HEAT_MID, high: HEAT_HIGH };
  }
  return { low: mixHex(hex, '#ffffff', 0.86), mid: hex, high: mixHex(hex, '#000000', 0.35) };
}
